import { useEffect } from 'react';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(useGSAP, ScrollTrigger);

const CALM = '(prefers-reduced-motion: no-preference)';

// Rolagem suave e leve; desligada para quem pede menos movimento
function useSmoothScroll() {
  useEffect(() => {
    if (!window.matchMedia(CALM).matches) return;

    const lenis = new Lenis({ lerp: 0.16, anchors: true });
    const tick = (time) => lenis.raf(time * 1000);
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(tick);
      lenis.destroy();
    };
  }, []);
}

// Toda a coreografia de rolagem. Sem JS ou com movimento reduzido, a página fica estática e legível.
export function useCinematicMotion(scope) {
  useSmoothScroll();

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      // Entradas: tocam uma vez, curtas e sem desfoque. O único momento autoral é a abertura das fotos.
      mm.add(CALM, () => {
        // opacity, não autoAlpha: o bloco ainda invisível continua alcançável por teclado e leitor de tela
        gsap.set('[data-reveal]', { opacity: 0, y: 14 });
        ScrollTrigger.batch('[data-reveal]', {
          start: 'top 90%',
          once: true,
          onEnter: (batch) => gsap.to(batch, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.05 }),
        });

        // Fotos: abrem de baixo para cima
        gsap.utils.toArray('[data-clip]').forEach((el) => {
          gsap.fromTo(
            el,
            { clipPath: 'inset(100% 0% 0% 0%)' },
            {
              clipPath: 'inset(0% 0% 0% 0%)',
              duration: 0.9,
              ease: 'power3.inOut',
              scrollTrigger: { trigger: el, start: 'top 85%', once: true },
            }
          );
        });
      });

      // Frase: cada palavra acende ao cruzar a linha de leitura, na ordem de leitura
      mm.add(CALM, () => {
        // Uma palavra por vez: o progresso da rolagem pela frase diz quantas já acenderam
        const words = gsap.utils.toArray('.tw');
        if (!words.length) return;
        ScrollTrigger.create({
          trigger: '.tagline-text',
          start: 'top 75%',
          end: 'bottom 45%',
          onUpdate: (self) => {
            const lit = Math.round(self.progress * words.length);
            words.forEach((w, i) => w.toggleAttribute('data-lit', i < lit));
          },
        });
      });

      // Profundidade leve, só em telas largas: a foto desliza dentro da moldura e o celular se endireita
      mm.add(`${CALM} and (min-width: 900px)`, () => {
        gsap.fromTo(
          '[data-tilt]',
          { rotateX: 10 },
          { rotateX: 0, ease: 'none', scrollTrigger: { trigger: '.phone-stage', start: 'top 70%', end: 'bottom 60%', scrub: 1 } }
        );

        gsap.utils.toArray('[data-parallax]').forEach((img) => {
          gsap.fromTo(
            img,
            { yPercent: -4, scale: 1.08 },
            {
              yPercent: 4,
              scale: 1.08,
              ease: 'none',
              scrollTrigger: { trigger: img.parentElement, start: 'top bottom', end: 'bottom top', scrub: 1 },
            }
          );
        });
      });

      // Serviços: o item que cruza o centro da tela acende, e a foto ao lado troca junto.
      // Fica fora do CALM: é estado, não movimento; o CSS tira as transições para quem pede menos movimento.
      mm.add('(min-width: 900px)', () => {
        const items = gsap.utils.toArray('[data-svc]');
        const shots = gsap.utils.toArray('[data-shot]');
        const activate = (i) => {
          items.forEach((el) => el.toggleAttribute('data-active', el.dataset.svc === String(i)));
          shots.forEach((el) => el.toggleAttribute('data-active', el.dataset.shot === String(i)));
        };
        items.forEach((item, i) =>
          ScrollTrigger.create({
            trigger: item,
            start: 'top center',
            end: 'bottom center',
            onToggle: (self) => self.isActive && activate(i),
          })
        );
      });
    },
    { scope }
  );
}
